// NOMBASE — Sentry initialisation (frontend).
//
// Called from index.js AFTER the runtime config (lib/runtime-config.js)
// has resolved. Sentry is init'd as the first thing inside the
// bootstrap, before React mounts, so its instrumentation hooks attach
// before any other module loads / throws.
//
// `dsn` is read from runtime config (Doppler → /api/config/public →
// frontend fetch on boot). The build-time `REACT_APP_SENTRY_DSN` is
// preserved as a fallback inside lib/runtime-config.js. If `dsn` is
// empty, init() is skipped — the SDK silently no-ops.
import * as Sentry from '@sentry/react';

let _initialised = false;

export function initSentry({ dsn } = {}) {
  if (_initialised || !dsn) return Sentry;

  const ENV = process.env.REACT_APP_SENTRY_ENVIRONMENT
    || (window.location.hostname === 'localhost' ? 'development' : 'production');
  const RELEASE = process.env.REACT_APP_SENTRY_RELEASE || 'nombase-frontend@1.0.0';

  Sentry.init({
    dsn,
    environment: ENV,
    release: RELEASE,

    // ── Performance tracing ───────────────────────────────────────
    // 10% of pageloads/navigations get a full transaction. Bump to 1.0
    // when actively debugging perf, then revert.
    tracesSampleRate: 0.1,

    // Connects frontend traces to backend traces. Only propagate the
    // sentry-trace header to OUR API (avoid leaking trace IDs to 3rd
    // parties like Clerk / Inngest / Cloudflare).
    tracePropagationTargets: [
      /^\//,                                    // same-origin
      /^https:\/\/mrp\.nombaseconnect\.com\//,  // production MRP
      /^https:\/\/api\.nombase\.com\//,         // future API subdomain
    ],

    integrations: [
      Sentry.browserTracingIntegration(),
      // Session Replay — locked-down "B2B / regulated data" defaults:
      // every text node masked, every input masked, every <img> blocked.
      // Replays show clicks + layout only. Safe for COA/PII surfaces.
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],

    // Replay sampling — record 10% of normal sessions, 100% of sessions
    // that end up throwing an error (the latter is the real value-add).
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Browser-side noise suppression — Clerk's <SignedOut> redirect
    // can trigger benign network aborts during auth bootstrap.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      // Clerk silent-refresh aborts during sign-out
      'AbortError: The operation was aborted',
    ],

    beforeSend(event) {
      // Strip Authorization headers if any sneak in (Sentry already
      // redacts most, this is belt-and-braces for Clerk JWTs).
      try {
        const headers = event?.request?.headers;
        if (headers) {
          for (const k of Object.keys(headers)) {
            if (['authorization', 'cookie', 'x-api-key'].includes(k.toLowerCase())) {
              headers[k] = '[Filtered]';
            }
          }
        }
      } catch (_) { /* noop */ }
      return event;
    },
  });

  _initialised = true;
  // eslint-disable-next-line no-console
  console.info('[Sentry] initialised', { environment: ENV, release: RELEASE });
  return Sentry;
}

export default Sentry;
